import { Alert, Backdrop, CircularProgress, Snackbar, Tooltip } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { LoadState } from "../../redux/common";
import { useAppDispatch } from "../../app/hooks";
import { useSelector } from "react-redux";
import { UserInfo, selectUser } from "../../redux/user/userSlice";
import { RetrieveUsersForPermissionsRequest, retrieveUsersForPermissions, selectUsersForPermissions, usersForPermissionsSlice } from "../../redux/user/usersForPermissionsSlice";
import { selectUserPermissionInsDel, userPermissionInsDelSlice } from "../../redux/user/userPermissionInsDelSlice";
import { PermissionId, checkIsAdmin } from "../../app/permissions";
import { isTokenReady } from "./UserAvatar";
import AddIcon from "@mui/icons-material/Add";
import UserPermissionsGrid from "./UserPermissionsGrid";
import ObjectUserAdd from "../base/page/user-add/ObjectUserAdd";
import KeyIcon from "@mui/icons-material/Key";
import ModalCreateInvite from "../base/page/user-add/ModalCreateInvite";

export interface UsersPermissionsContainerProps {
    canGrantPermission: boolean;
    targetId: string;
    permissions: PermissionId[];
    messages: {
        title: string;
        empty: string;
        loadError?: string;
    }
}

const UsersPermissionsContainer = (props: UsersPermissionsContainerProps) => {
    const dispatch = useAppDispatch();

    const user = useSelector(selectUser);
    const usersForPermissions = useSelector(selectUsersForPermissions);
    const userPermissionInsDel = useSelector(selectUserPermissionInsDel);
    const isAdmin = checkIsAdmin(user.priza.info);
    const [inserting, setInserting] = useState(false);
    const [generatingRedeem, setGeneratingRedeem] = useState(false);

    const usersForPermissionsRequest = useMemo(() => {
        const ret: RetrieveUsersForPermissionsRequest = { targetId: props.targetId, permissions: props.permissions };
        return ret;
    }, [props.targetId, props.permissions]);

    useEffect(() => {
        dispatch(usersForPermissionsSlice.actions.checkLoaded(usersForPermissionsRequest));
        if (usersForPermissions.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveUsersForPermissions(usersForPermissionsRequest));
        }
    }, [usersForPermissionsRequest, usersForPermissions, user]);

    const list = useMemo(() => { return usersForPermissions.list || [] as UserInfo[]; }, [usersForPermissions.list]);

    const buttons = [];
    if(props.canGrantPermission) {
        buttons.push({
            label: <AddIcon />,
            click: () => {
                setInserting(true);
            }
        });
    }
    if(isAdmin) {
        buttons.push({
            label: <Tooltip title="Gerar link de convite">
                <KeyIcon />
            </Tooltip>,
            click: () => {
                setGeneratingRedeem(true);
            }
        });
    }

    const dismiss = () => {
        if(userPermissionInsDel.state.state == LoadState.Loaded || userPermissionInsDel.state.state == LoadState.Failed)
            dispatch(userPermissionInsDelSlice.actions.clean());
    };

    if(inserting) {
        return <ObjectUserAdd
                targetId={props.targetId}
                permissions={props.permissions}
                back={() => { 
                    dispatch(usersForPermissionsSlice.actions.clean());
                    setInserting(false);
                }}
            />;
    }

    return <>
        <UserPermissionsGrid
            list={list}
            targetId={props.targetId}
            permissions={props.permissions}
            toolbar={{
                label: props.messages.title + ` (${list.length})`,
                buttons: buttons
            }}
            state={usersForPermissions.state}
            retry={() => { dispatch(usersForPermissionsSlice.actions.clean()); }}
            messages={{
                empty: props.messages.empty,
                error: props.messages.loadError || "Erro ao carregar permissões."
            }} />
        <Snackbar open={userPermissionInsDel.state.state == LoadState.Failed} autoHideDuration={3000} onClose={dismiss}>
            <Alert onClose={dismiss} severity="error" variant="filled" sx={{ width: "100%" }}> Erro ao atualizar permissão. </Alert>
        </Snackbar>
        <Snackbar open={userPermissionInsDel.state.state == LoadState.Loaded} autoHideDuration={1000} onClose={dismiss}>
            <Alert onClose={dismiss} severity="success" variant="filled" sx={{ width: "100%" }}> Permissão salva. </Alert>
        </Snackbar>
        <Backdrop open={userPermissionInsDel.state.state == LoadState.Loading} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
            <CircularProgress color="inherit" />
        </Backdrop>
        <ModalCreateInvite 
            open={generatingRedeem} close={() => setGeneratingRedeem(false)}
            permissions={props.permissions} targetId={props.targetId}
        />
    </>;
};


export default UsersPermissionsContainer;